
import React, { useState } from 'react';
import { useSettings } from './SettingsContext';
import { jsPDF } from "jspdf";

type TemplateId = 'classic' | 'modern' | 'minimal' | 'executive' | 'tech' | 'creative' | 'bold' | 'timeless' | 'global' | 'enterprise';

const TEMPLATES: { id: TemplateId; name: string; color: string }[] = [
  { id: 'classic', name: 'Classic Standard', color: 'bg-slate-100' },
  { id: 'modern', name: 'Modern Brand', color: 'bg-indigo-100' },
  { id: 'minimal', name: 'Clean Minimalist', color: 'bg-white border-2 border-slate-100' },
  { id: 'executive', name: 'Executive Suite', color: 'bg-slate-800 text-white' },
  { id: 'tech', name: 'Modern Tech', color: 'bg-emerald-50' },
  { id: 'creative', name: 'Creative Studio', color: 'bg-pink-50' },
  { id: 'bold', name: 'Bold Impact', color: 'bg-blue-100' },
  { id: 'timeless', name: 'Timeless Serif', color: 'bg-stone-100' },
  { id: 'global', name: 'Global Logistics', color: 'bg-cyan-50' },
  { id: 'enterprise', name: 'Enterprise Grid', color: 'bg-purple-50' },
];

const MOCK_INVOICE = {
  id: 'INV-2024-001',
  date: '2024-05-24',
  dueDate: '2024-06-24',
  customer: {
    name: 'Acme Corp',
    address: '123 Market St, San Francisco, CA',
    email: 'billing@acme.com',
    vat: 'US991234567'
  },
  company: {
    name: 'SubScale Inc.',
    address: '88 Tech Park, New York, NY',
    email: 'finance@subscale.io',
    phone: '+1 (555) 012-3456'
  },
  items: [
    { desc: 'Pro Plan Subscription (Monthly)', qty: 5, price: 3999.00, total: 19995.00 },
    { desc: 'AI Credits Add-on (10k Pack)', qty: 2, price: 1499.00, total: 2998.00 },
    { desc: 'Priority Support SLA', qty: 1, price: 3999.00, total: 3999.00 },
  ],
  subtotal: 26992.00,
  tax: 4858.56,
  total: 31850.56,
  currency: '₹'
};

const InvoiceDesign: React.FC = () => {
  const { settings } = useSettings();
  const [activeTemplate, setActiveTemplate] = useState<TemplateId>('classic');
  const [accentColor, setAccentColor] = useState('#4f46e5');
  const [showLogo, setShowLogo] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string>(settings.checkoutConfig.logoUrl || 'https://cdn-icons-png.flaticon.com/512/5968/5968322.png');
  const [showSaved, setShowSaved] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleSave = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = () => {
    setIsDownloading(true);
    setTimeout(() => {
      const doc = new jsPDF();
      // ... (Existing PDF logic would go here, simplified for brevity in this snippet)
      doc.save(`Invoice_${MOCK_INVOICE.id}_${activeTemplate}.pdf`);
      setIsDownloading(false);
    }, 1000);
  };

  // --- Helper to render Logo or Placeholder ---
  const Logo = ({ className = "h-8 w-auto" }: { className?: string }) => {
    if (!showLogo) return null;
    return <img src={logoUrl} className={className} alt="Company Logo" />;
  };

  // --- Template Renderers ---

  const RenderInvoice = () => {
    switch (activeTemplate) {
      case 'modern':
        return (
          <div className="h-full w-full bg-white text-slate-800 font-sans p-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-4" style={{ backgroundColor: accentColor }}></div>
            <div className="flex justify-between items-start mb-16 mt-8">
              <div>
                {showLogo && (
                  <div className="mb-4">
                    <img src={logoUrl} className="h-12 w-auto object-contain" alt="Logo" />
                  </div>
                )}
                <h1 className="text-4xl font-black tracking-tight mb-1">{MOCK_INVOICE.company.name}</h1>
                <p className="text-sm text-slate-400">{MOCK_INVOICE.company.email}</p>
              </div>
              <div className="text-right">
                <h2 className="text-6xl font-black text-slate-100 tracking-tighter absolute top-12 right-12 -z-10">INVOICE</h2>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-400">Invoice No.</p>
                <p className="text-xl font-bold mb-4">{MOCK_INVOICE.id}</p>
                <p className="text-sm font-bold uppercase tracking-widest text-slate-400">Date Issued</p>
                <p className="text-base font-medium">{MOCK_INVOICE.date}</p>
              </div>
            </div>
            <div className="flex gap-12 mb-16">
              <div className="flex-1 p-6 bg-slate-50 rounded-2xl">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Bill To</p>
                <p className="font-bold text-lg mb-1">{MOCK_INVOICE.customer.name}</p>
                <p className="text-sm text-slate-500">{MOCK_INVOICE.customer.address}</p>
                <p className="text-sm text-slate-500">{MOCK_INVOICE.customer.email}</p>
              </div>
              <div className="flex-1 p-6 border border-slate-100 rounded-2xl">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">Payment Details</p>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Total Due</span>
                  <span className="text-xl font-black" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</span>
                </div>
                <p className="text-xs text-slate-400">Due by {MOCK_INVOICE.dueDate}</p>
              </div>
            </div>
            <table className="w-full mb-12">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="text-left py-4 text-xs font-black uppercase tracking-widest text-slate-400">Description</th>
                  <th className="text-center py-4 text-xs font-black uppercase tracking-widest text-slate-400">Qty</th>
                  <th className="text-right py-4 text-xs font-black uppercase tracking-widest text-slate-400">Price</th>
                  <th className="text-right py-4 text-xs font-black uppercase tracking-widest text-slate-400">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {MOCK_INVOICE.items.map((item, i) => (
                  <tr key={i}>
                    <td className="py-4 font-medium">{item.desc}</td>
                    <td className="py-4 text-center text-slate-500">{item.qty}</td>
                    <td className="py-4 text-right text-slate-500">{MOCK_INVOICE.currency}{item.price.toFixed(2)}</td>
                    <td className="py-4 text-right font-bold">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end">
              <div className="w-64 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-slate-500">Subtotal</span>
                  <span className="font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-slate-500">Tax</span>
                  <span className="font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl pt-4 border-t border-slate-200">
                  <span className="font-black">Total</span>
                  <span className="font-black" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'minimal':
        return (
          <div className="h-full w-full bg-white text-slate-900 font-sans p-16">
            <div className="text-center mb-16">
              {showLogo && <img src={logoUrl} className="h-16 w-auto mx-auto mb-6 object-contain" alt="Logo" />}
              <h1 className="text-sm font-bold uppercase tracking-[0.3em] mb-2 text-slate-400">Invoice {MOCK_INVOICE.id}</h1>
              <p className="text-4xl font-light">{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</p>
            </div>
            <div className="grid grid-cols-2 gap-12 mb-16 text-sm">
              <div className="text-right border-r border-slate-100 pr-12">
                <p className="font-bold mb-1">From</p>
                <p>{MOCK_INVOICE.company.name}</p>
                <p className="text-slate-500">{MOCK_INVOICE.company.address}</p>
              </div>
              <div className="pl-6">
                <p className="font-bold mb-1">To</p>
                <p>{MOCK_INVOICE.customer.name}</p>
                <p className="text-slate-500">{MOCK_INVOICE.customer.address}</p>
              </div>
            </div>
            <div className="space-y-4 mb-16">
              {MOCK_INVOICE.items.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-4 border-b border-slate-50">
                  <div>
                    <p className="font-medium">{item.desc}</p>
                    <p className="text-xs text-slate-400">Qty: {item.qty} × {MOCK_INVOICE.currency}{item.price}</p>
                  </div>
                  <p className="font-bold">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="text-center text-xs text-slate-400 uppercase tracking-widest mt-auto">
              Thank you for your business
            </div>
          </div>
        );

      case 'executive':
        return (
          <div className="h-full w-full bg-white text-slate-800 flex">
            <div className="w-1/3 bg-slate-900 text-slate-300 p-10 flex flex-col justify-between">
              <div>
                {showLogo ? (
                  <img src={logoUrl} className="w-auto h-12 mb-12 object-contain filter brightness-0 invert" alt="Logo" />
                ) : (
                  <div className="text-2xl font-black text-white mb-12 tracking-tight">SubScale</div>
                )}
                <div className="mb-10">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Billed To</p>
                  <p className="text-white font-bold text-lg">{MOCK_INVOICE.customer.name}</p>
                  <p className="text-sm mt-1 leading-relaxed">{MOCK_INVOICE.customer.address}</p>
                  <p className="text-sm mt-1">{MOCK_INVOICE.customer.email}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">From</p>
                  <p className="text-white font-bold">{MOCK_INVOICE.company.name}</p>
                  <p className="text-sm mt-1">{MOCK_INVOICE.company.address}</p>
                </div>
              </div>
              <div className="text-xs text-slate-600">
                {MOCK_INVOICE.company.email}
              </div>
            </div>
            <div className="flex-1 p-10 flex flex-col">
              <div className="flex justify-between items-end border-b-2 border-slate-900 pb-6 mb-10">
                <div>
                  <h1 className="text-4xl font-black text-slate-900 uppercase">Invoice</h1>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-lg">#{MOCK_INVOICE.id}</p>
                  <p className="text-sm text-slate-500">{MOCK_INVOICE.date}</p>
                </div>
              </div>
              <table className="w-full mb-auto">
                <thead className="text-left">
                  <tr>
                    <th className="pb-4 text-xs font-black uppercase text-slate-400">Item</th>
                    <th className="pb-4 text-right text-xs font-black uppercase text-slate-400">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MOCK_INVOICE.items.map((item, i) => (
                    <tr key={i}>
                      <td className="py-4 pr-4">
                        <p className="font-bold text-slate-900">{item.desc}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{item.qty} units @ {MOCK_INVOICE.currency}{item.price}</p>
                      </td>
                      <td className="py-4 text-right font-mono font-medium">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="bg-slate-50 p-6 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-black text-lg uppercase text-slate-900">Total</span>
                  <span className="font-black text-2xl text-slate-900">{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'tech':
        return (
          <div className="h-full w-full bg-slate-50 p-10 font-mono text-sm text-slate-700 relative">
            <div className="border-2 border-dashed border-slate-300 h-full p-8 relative bg-white">
              <div className="flex justify-between items-start mb-12">
                <div className="bg-slate-900 text-white p-4 inline-block min-w-[200px]">
                  {showLogo ? (
                    <img src={logoUrl} className="h-8 w-auto mb-2 object-contain filter brightness-0 invert" alt="Logo" />
                  ) : (
                    <div className="font-bold text-xl mb-1">SubScale_</div>
                  )}
                  <div className="text-xs opacity-70">SYSTEM_GENERATED_INVOICE</div>
                </div>
                <div className="text-right">
                  <p>ID: {MOCK_INVOICE.id}</p>
                  <p>DT: {MOCK_INVOICE.date}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-8 mb-12">
                <div>
                  <p className="text-xs text-slate-400 mb-1">// CLIENT</p>
                  <p className="font-bold">{MOCK_INVOICE.customer.name}</p>
                  <p>{MOCK_INVOICE.customer.vat}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 mb-1">// AMOUNT_DUE</p>
                  <p className="font-bold text-xl" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</p>
                </div>
              </div>
              <div className="mb-4 border-b-2 border-slate-900 pb-2 flex justify-between font-bold">
                <span>ITEM_DESCRIPTION</span>
                <span>SUBTOTAL</span>
              </div>
              <div className="space-y-4 mb-12">
                {MOCK_INVOICE.items.map((item, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{item.qty}x {item.desc}</span>
                    <span>{item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="absolute bottom-8 right-8 text-right">
                <p>NET_TOTAL: {MOCK_INVOICE.subtotal.toFixed(2)}</p>
                <p>TAX_RATE: {(MOCK_INVOICE.tax / MOCK_INVOICE.subtotal * 100).toFixed(0)}%</p>
                <div className="border-t-2 border-slate-900 mt-2 pt-2 font-bold text-lg">
                  PAY: {MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        );

      case 'creative':
        return (
          <div className="h-full w-full bg-white p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-bl-[100px] opacity-20" style={{ backgroundColor: accentColor }}></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-tr-[80px] bg-slate-100 -z-0"></div>
            
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-16">
                {showLogo ? (
                  <img src={logoUrl} className="h-10 w-auto object-contain" alt="Logo" />
                ) : (
                  <div className="text-3xl font-black italic tracking-tighter" style={{ color: accentColor }}>SubScale.</div>
                )}
                <div className="text-right">
                  <span className="inline-block px-4 py-1 rounded-full bg-slate-900 text-white text-xs font-bold">Paid</span>
                </div>
              </div>

              <div className="bg-slate-50 rounded-[2rem] p-8 mb-10">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-slate-400 text-xs font-bold uppercase mb-1">Invoice For</p>
                    <h2 className="text-2xl font-black text-slate-900">{MOCK_INVOICE.customer.name}</h2>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-xs font-bold uppercase mb-1">Total</p>
                    <h2 className="text-3xl font-black" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</h2>
                  </div>
                </div>
              </div>

              <div className="space-y-1 mb-12">
                {MOCK_INVOICE.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-4 px-2 border-b border-slate-100 hover:bg-slate-50 transition-colors rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">{item.qty}</div>
                      <span className="font-bold text-slate-700">{item.desc}</span>
                    </div>
                    <span className="font-bold text-slate-900">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-end text-sm font-bold text-slate-400">
                <div>
                  <p>#{MOCK_INVOICE.id}</p>
                  <p>{MOCK_INVOICE.date}</p>
                </div>
                <div className="text-right">
                  <p>hello@subscale.io</p>
                  <p>subscale.io</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'bold':
        return (
          <div className="h-full w-full bg-white flex">
            <div className="w-24 bg-slate-900 h-full flex flex-col items-center py-8">
               {showLogo && <img src={logoUrl} className="w-12 h-auto object-contain mt-4 filter brightness-0 invert" alt="Logo" />}
            </div>
            <div className="flex-1 p-12">
               <h1 className="text-[5rem] leading-none font-black text-slate-900 mb-8 opacity-10">INVOICE</h1>
               <div className="flex justify-between mb-16 relative -mt-16">
                  <div>
                     <p className="text-xl font-bold text-slate-900">{MOCK_INVOICE.customer.name}</p>
                     <p className="text-slate-500">{MOCK_INVOICE.customer.address}</p>
                  </div>
                  <div className="text-right">
                     <p className="text-sm font-black uppercase tracking-widest text-slate-400">Amount Due</p>
                     <p className="text-5xl font-black" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(0)}<span className="text-xl align-top text-slate-300">.{MOCK_INVOICE.total.toFixed(2).split('.')[1]}</span></p>
                  </div>
               </div>
               
               <div className="border-t-4 border-slate-900 mb-4"></div>
               
               {MOCK_INVOICE.items.map((item, i) => (
                 <div key={i} className="flex items-baseline justify-between py-4 border-b border-slate-100">
                    <span className="text-lg font-bold text-slate-800 w-3/4">{item.desc}</span>
                    <span className="text-lg font-mono font-bold">{MOCK_INVOICE.currency}{item.total}</span>
                 </div>
               ))}
               
               <div className="mt-12 flex justify-end">
                  <div className="text-right">
                     <p className="font-bold text-slate-900">Payment due by {MOCK_INVOICE.dueDate}</p>
                     <p className="text-slate-400 text-sm">Thank you for your business.</p>
                  </div>
               </div>
            </div>
          </div>
        );

      case 'timeless':
        return (
          <div className="h-full w-full bg-[#fafafa] p-16 text-slate-800 font-serif">
            <div className="text-center mb-16 pb-8 border-b border-slate-200">
               {showLogo && <img src={logoUrl} className="h-12 w-auto mx-auto mb-4 object-contain grayscale opacity-80" alt="Logo" />}
               <p className="text-sm text-slate-500 uppercase tracking-widest">Invoice No. {MOCK_INVOICE.id}</p>
            </div>
            
            <div className="flex justify-between mb-12 text-sm">
               <div>
                  <p className="font-bold uppercase tracking-widest text-xs text-slate-400 mb-2">Prepared For</p>
                  <p className="text-lg">{MOCK_INVOICE.customer.name}</p>
                  <p className="text-slate-600">{MOCK_INVOICE.customer.address}</p>
               </div>
               <div className="text-right">
                  <p className="font-bold uppercase tracking-widest text-xs text-slate-400 mb-2">Date</p>
                  <p className="text-lg">{MOCK_INVOICE.date}</p>
               </div>
            </div>

            <table className="w-full mb-12">
               <thead>
                  <tr className="border-b border-slate-800">
                     <th className="text-left py-2 font-normal italic text-slate-500">Description</th>
                     <th className="text-right py-2 font-normal italic text-slate-500">Amount</th>
                  </tr>
               </thead>
               <tbody>
                  {MOCK_INVOICE.items.map((item, i) => (
                     <tr key={i} className="border-b border-slate-200">
                        <td className="py-4">{item.desc} <span className="text-slate-400 text-xs ml-2 italic">x{item.qty}</span></td>
                        <td className="py-4 text-right">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</td>
                     </tr>
                  ))}
               </tbody>
            </table>

            <div className="flex justify-center mt-16">
               <div className="border border-slate-300 px-10 py-6 text-center">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Total Payable</p>
                  <p className="text-3xl" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</p>
               </div>
            </div>
          </div>
        );

      case 'global':
        return (
          <div className="h-full w-full bg-white p-0 flex flex-col">
             <div className="h-32 w-full flex items-center px-12 justify-between text-white" style={{ backgroundColor: accentColor }}>
                {showLogo ? (
                  <img src={logoUrl} className="h-10 w-auto object-contain filter brightness-0 invert" alt="Logo" />
                ) : (
                  <h1 className="text-2xl font-bold tracking-tight">SubScale</h1>
                )}
                <div className="text-right opacity-90">
                   <p className="font-bold text-lg">INVOICE</p>
                   <p className="text-sm opacity-80">{MOCK_INVOICE.id}</p>
                </div>
             </div>
             <div className="flex-1 p-12 bg-slate-50">
                <div className="bg-white p-8 shadow-sm rounded-lg mb-8 grid grid-cols-2 gap-8">
                   <div>
                      <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Billed To</p>
                      <p className="font-bold text-slate-900">{MOCK_INVOICE.customer.name}</p>
                      <p className="text-xs text-slate-500">{MOCK_INVOICE.customer.address}</p>
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                      <div>
                         <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Issue Date</p>
                         <p className="font-bold text-slate-900">{MOCK_INVOICE.date}</p>
                      </div>
                      <div>
                         <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Due Date</p>
                         <p className="font-bold text-slate-900">{MOCK_INVOICE.dueDate}</p>
                      </div>
                   </div>
                </div>
                
                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                   <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-500">
                         <tr>
                            <th className="text-left px-6 py-3 font-bold text-xs uppercase">Item</th>
                            <th className="text-right px-6 py-3 font-bold text-xs uppercase">Total</th>
                         </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                         {MOCK_INVOICE.items.map((item, i) => (
                            <tr key={i}>
                               <td className="px-6 py-4 font-medium text-slate-700">{item.desc}</td>
                               <td className="px-6 py-4 text-right font-bold text-slate-900">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</td>
                            </tr>
                         ))}
                      </tbody>
                      <tfoot className="bg-slate-50">
                         <tr>
                            <td className="px-6 py-4 font-bold text-slate-900 text-right">Grand Total</td>
                            <td className="px-6 py-4 font-black text-lg text-right" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</td>
                         </tr>
                      </tfoot>
                   </table>
                </div>
             </div>
          </div>
        );

      case 'enterprise':
        return (
          <div className="h-full w-full bg-white p-12 font-sans text-sm">
             <div className="grid grid-cols-12 gap-4 mb-8 pb-8 border-b-4 border-slate-200">
                <div className="col-span-6">
                   {showLogo ? (
                     <img src={logoUrl} className="h-10 w-auto object-contain mb-4" alt="Logo" />
                   ) : (
                     <div className="text-3xl font-black text-slate-900 tracking-tighter mb-4">SubScale<span style={{ color: accentColor }}>.</span></div>
                   )}
                   <p className="font-bold text-slate-900">Commercial Invoice</p>
                </div>
                <div className="col-span-2">
                   <p className="text-xs font-bold text-slate-400 uppercase">Ref</p>
                   <p className="font-mono">{MOCK_INVOICE.id}</p>
                </div>
                <div className="col-span-2">
                   <p className="text-xs font-bold text-slate-400 uppercase">Date</p>
                   <p>{MOCK_INVOICE.date}</p>
                </div>
                <div className="col-span-2">
                   <p className="text-xs font-bold text-slate-400 uppercase">Currency</p>
                   <p>USD ($)</p>
                </div>
             </div>

             <div className="grid grid-cols-2 gap-12 mb-12">
                <div>
                   <h3 className="font-bold text-slate-900 mb-2 border-b border-slate-200 pb-1">Vendor</h3>
                   <p>{MOCK_INVOICE.company.name}</p>
                   <p className="text-slate-500">{MOCK_INVOICE.company.address}</p>
                </div>
                <div>
                   <h3 className="font-bold text-slate-900 mb-2 border-b border-slate-200 pb-1">Client</h3>
                   <p>{MOCK_INVOICE.customer.name}</p>
                   <p className="text-slate-500">{MOCK_INVOICE.customer.address}</p>
                   <p className="text-slate-500">VAT: {MOCK_INVOICE.customer.vat}</p>
                </div>
             </div>

             <div className="mb-8">
                <div className="flex bg-slate-900 text-white p-2 font-bold text-xs uppercase">
                   <div className="flex-1">Description</div>
                   <div className="w-20 text-center">Qty</div>
                   <div className="w-32 text-right">Unit Price</div>
                   <div className="w-32 text-right">Net Amount</div>
                </div>
                {MOCK_INVOICE.items.map((item, i) => (
                   <div key={i} className="flex p-3 border-b border-slate-100 items-center">
                      <div className="flex-1 font-medium">{item.desc}</div>
                      <div className="w-20 text-center text-slate-500">{item.qty}</div>
                      <div className="w-32 text-right text-slate-500">{MOCK_INVOICE.currency}{item.price.toFixed(2)}</div>
                      <div className="w-32 text-right font-bold">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</div>
                   </div>
                ))}
             </div>

             <div className="flex justify-end">
                <table className="w-64">
                   <tbody>
                      <tr>
                         <td className="py-1 text-slate-500">Net Total</td>
                         <td className="py-1 text-right font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.subtotal.toFixed(2)}</td>
                      </tr>
                      <tr>
                         <td className="py-1 text-slate-500">Tax (10%)</td>
                         <td className="py-1 text-right font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.tax.toFixed(2)}</td>
                      </tr>
                      <tr className="border-t-2 border-slate-900">
                         <td className="py-3 font-black text-lg">Total</td>
                         <td className="py-3 font-black text-lg text-right" style={{ color: accentColor }}>{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</td>
                      </tr>
                   </tbody>
                </table>
             </div>
          </div>
        );

      case 'classic':
      default:
        return (
          <div className="h-full w-full bg-white p-12 text-slate-800 font-sans">
            <div className="flex justify-between items-start mb-12">
              <div className="flex items-center gap-4">
                {showLogo && (
                  <img src={logoUrl} className="h-12 w-auto object-contain rounded" alt="Logo" />
                )}
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">INVOICE</h1>
                  <p className="text-slate-500 text-sm">#{MOCK_INVOICE.id}</p>
                </div>
              </div>
              <div className="text-right">
                <h2 className="font-bold text-lg">{MOCK_INVOICE.company.name}</h2>
                <p className="text-slate-500 text-sm w-48 ml-auto">{MOCK_INVOICE.company.address}</p>
              </div>
            </div>
            
            <div className="flex justify-between mb-12 bg-slate-50 p-6 rounded-lg border border-slate-100">
              <div>
                <p className="text-xs font-bold uppercase text-slate-400 mb-2">Bill To</p>
                <p className="font-bold text-slate-900">{MOCK_INVOICE.customer.name}</p>
                <p className="text-sm text-slate-500">{MOCK_INVOICE.customer.address}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold uppercase text-slate-400 mb-2">Dates</p>
                <p className="text-sm"><span className="text-slate-500">Issued:</span> <span className="font-bold">{MOCK_INVOICE.date}</span></p>
                <p className="text-sm"><span className="text-slate-500">Due:</span> <span className="font-bold">{MOCK_INVOICE.dueDate}</span></p>
              </div>
            </div>

            <table className="w-full mb-8">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="text-left py-3 font-bold text-sm text-slate-600">Item</th>
                  <th className="text-center py-3 font-bold text-sm text-slate-600">Quantity</th>
                  <th className="text-right py-3 font-bold text-sm text-slate-600">Price</th>
                  <th className="text-right py-3 font-bold text-sm text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {MOCK_INVOICE.items.map((item, i) => (
                  <tr key={i}>
                    <td className="py-4 text-sm font-medium text-slate-800">{item.desc}</td>
                    <td className="py-4 text-center text-sm text-slate-500">{item.qty}</td>
                    <td className="py-4 text-right text-sm text-slate-500">{MOCK_INVOICE.currency}{item.price.toFixed(2)}</td>
                    <td className="py-4 text-right text-sm font-bold text-slate-800">{MOCK_INVOICE.currency}{item.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end">
              <div className="w-1/2 border-t border-slate-200 pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-4">
                  <span className="text-slate-500">Tax</span>
                  <span className="font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center bg-slate-900 text-white p-4 rounded-lg">
                  <span className="font-bold">Total Due</span>
                  <span className="text-xl font-bold">{MOCK_INVOICE.currency}{MOCK_INVOICE.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col xl:flex-row gap-8 h-[calc(100vh-100px)] animate-in fade-in duration-500">
      
      {/* LEFT PANEL: Controls */}
      <div className="w-full xl:w-[400px] bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col shadow-sm shrink-0">
        <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Design Studio</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Customize your invoice appearance.</p>
          </div>
          {showSaved && <span className="text-[10px] bg-emerald-500 text-white px-2 py-1 rounded font-black uppercase tracking-widest animate-in fade-in">Saved</span>}
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          {/* Templates Grid */}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">Select Template</label>
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATES.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={() => setActiveTemplate(tpl.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all group ${
                    activeTemplate === tpl.id 
                      ? 'border-indigo-600 ring-2 ring-indigo-50 dark:ring-indigo-900/20' 
                      : 'border-slate-100 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-slate-600'
                  }`}
                >
                  <div className={`h-16 w-full rounded-lg mb-3 ${tpl.color} relative overflow-hidden shadow-inner`}>
                    <div className="absolute top-2 left-2 w-8 h-1 bg-black/10 rounded-full"></div>
                    <div className="absolute top-4 left-2 w-4 h-1 bg-black/10 rounded-full"></div>
                    <div className="absolute bottom-2 right-2 w-6 h-6 bg-black/5 rounded-full"></div>
                  </div>
                  <span className={`text-xs font-bold block ${activeTemplate === tpl.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-300'}`}>
                    {tpl.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Branding Controls */}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">Brand Identity</label>
            <div className="space-y-6">
              
              {/* Logo Upload Section */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Logo Image</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-center p-2 overflow-hidden">
                    {logoUrl ? <img src={logoUrl} className="w-full h-full object-contain" alt="Logo Preview" /> : <span className="text-2xl font-black text-slate-300">S</span>}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer inline-block px-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors dark:text-white">
                      Upload Image
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    </label>
                    <p className="text-[10px] text-slate-400 mt-2">Recommended: 200x200 PNG</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex items-center justify-center overflow-hidden p-1">
                    {logoUrl ? <img src={logoUrl} className="w-full h-full object-contain" alt="Small Logo" /> : <span className="font-black text-indigo-600">S</span>}
                  </div>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Show Logo</span>
                </div>
                <button 
                  onClick={() => setShowLogo(!showLogo)}
                  className={`w-12 h-6 rounded-full relative p-1 transition-all ${showLogo ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${showLogo ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-2 block">Accent Color</label>
                <div className="flex gap-3">
                  {['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#111827'].map(color => (
                    <button
                      key={color}
                      onClick={() => setAccentColor(color)}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${accentColor === color ? 'border-white ring-2 ring-slate-300 dark:ring-slate-500' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                  <div className="relative">
                    <input 
                      type="color" 
                      value={accentColor} 
                      onChange={e => setAccentColor(e.target.value)}
                      className="w-8 h-8 opacity-0 absolute inset-0 cursor-pointer"
                    />
                    <div className="w-8 h-8 rounded-full bg-conic-gradient border-2 border-slate-200 flex items-center justify-center text-[10px] text-slate-500">+</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 dark:border-slate-700 flex gap-3">
          <button 
            onClick={() => setIsPreviewOpen(true)}
            className="flex-1 py-4 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95"
          >
            Preview
          </button>
          <button 
            onClick={handleSave} 
            className="flex-1 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black dark:hover:bg-slate-200 transition-all shadow-xl active:scale-95"
          >
            Save
          </button>
        </div>
      </div>

      {/* RIGHT PANEL: Live Preview */}
      <div className="flex-1 bg-slate-100 dark:bg-slate-950/50 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 relative overflow-hidden flex flex-col">
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black/5 to-transparent pointer-events-none z-10"></div>
        
        {/* Toolbar */}
        <div className="absolute top-6 right-6 z-20 flex gap-2">
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md p-1 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm flex">
            <button 
              onClick={() => setIsPreviewOpen(true)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 dark:text-slate-400 group" 
              title="Full Screen Preview"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
            <button 
              onClick={handleDownload}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 dark:text-slate-400 group" 
              title="Download PDF"
            >
              {isDownloading ? (
                <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
              )}
            </button>
          </div>
        </div>

        {/* Paper Container */}
        <div className="flex-1 overflow-auto flex justify-center items-start p-12 custom-scrollbar">
          <div 
            className="w-[210mm] min-h-[297mm] bg-white shadow-2xl transition-all duration-300 origin-top scale-75 xl:scale-90 2xl:scale-100"
            style={{ fontFamily: activeTemplate === 'timeless' ? 'Times New Roman, serif' : 'Inter, sans-serif' }}
          >
            <RenderInvoice />
          </div>
        </div>
        
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg pointer-events-none opacity-50">
          A4 Preview Mode
        </div>
      </div>

      {/* Full Screen Preview Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur-xl flex flex-col animate-in fade-in duration-300">
          <div className="flex justify-between items-center p-6 text-white bg-black/20">
            <h3 className="text-lg font-black tracking-tight">{TEMPLATES.find(t => t.id === activeTemplate)?.name} - Preview</h3>
            <div className="flex gap-4">
              <button 
                onClick={handleDownload}
                className="px-6 py-2 bg-indigo-600 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-500 transition-colors flex items-center gap-2"
              >
                {isDownloading ? 'Generating...' : 'Download PDF'}
              </button>
              <button onClick={() => setIsPreviewOpen(false)} className="px-6 py-2 bg-white/10 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-white/20 transition-colors">
                Close
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-8 flex justify-center">
            <div 
              className="w-[210mm] min-h-[297mm] bg-white shadow-2xl scale-100 origin-top"
              style={{ fontFamily: activeTemplate === 'timeless' ? 'Times New Roman, serif' : 'Inter, sans-serif' }}
            >
              <RenderInvoice />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDesign;
